import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { Schema } from "effect";
import { decodeUnknownOption } from "../../../packages/common/src/schema.ts";
import { ApiError } from "#api.ts";
import { useAuth } from "#auth.tsx";
import {
  mergeOrders,
  Order,
  orderSchema,
  type OrderState,
} from "#orders.ts";
import { OrderEvents, type ConnectionState } from "#order-events.ts";
import {
  activeOrdersQueryKey,
  activeOrdersQueryOptions,
} from "#orders.query.ts";

type OrdersState = {
  readonly orders: readonly Order[];
  readonly query: UseQueryResult<readonly Order[], ApiError>;
  readonly connection: ConnectionState;
  readonly notice: string | undefined;
  readonly setNotice: (notice: string | undefined) => void;
  readonly refresh: () => Promise<void>;
  readonly merge: (orders: readonly Order[]) => void;
  readonly closure: ClosedOrderChange | undefined;
  readonly recordClosure: (previous: Order, saved: Order) => void;
  readonly dismissClosure: (revision: string) => void;
};

/** A confirmed operational closure and its precise revision, safe to conditionally undo. */
export type ClosedOrderChange = {
  readonly order: Order;
  readonly previousState: OrderState;
  readonly revision: string;
};

const OrdersContext = createContext<OrdersState | undefined>(undefined);

const eventSchema = Schema.Struct({
  type: Schema.Literal("order.updated"),
  payload: orderSchema,
});

/** One live subscription per staff application; active queues paginate to completion. */
export function OrdersProvider({ children }: { readonly children: ReactNode }) {
  const { api, state } = useAuth();
  const queryClient = useQueryClient();
  const ordersQuery = useQuery(
    activeOrdersQueryOptions(
      api,
      queryClient,
      state.kind === "authenticated",
    ),
  );
  const orders = ordersQuery.data ?? [];
  const refetchOrders = ordersQuery.refetch;
  const refreshState = useRef<{
    running: Promise<void> | undefined;
    queued: boolean;
  }>({ running: undefined, queued: false });
  const [notice, setNotice] = useState<string>();
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [closure, setClosure] = useState<ClosedOrderChange>();

  const recordClosure = useCallback((previous: Order, saved: Order) => {
    if (
      saved.revision &&
      previous.id === saved.id &&
      previous.state !== saved.state &&
      (saved.state === "order_completed" || saved.state === "order_canceled")
    )
      setClosure({
        order: saved,
        previousState: previous.state,
        revision: saved.revision,
      });
  }, []);

  const dismissClosure = useCallback((revision: string) => {
    setClosure((current) =>
      current?.revision === revision ? undefined : current,
    );
  }, []);

  const merge = useCallback(
    (incoming: readonly Order[]) =>
      queryClient.setQueryData<readonly Order[]>(
        activeOrdersQueryKey,
        (existing = []) => mergeOrders(existing, incoming),
      ),
    [queryClient],
  );

  const refresh = useCallback(() => {
    const state = refreshState.current;

    if (state.running) {
      state.queued = true;
      return state.running;
    }

    const run = async () => {
      do {
        state.queued = false;
        await refetchOrders({ cancelRefetch: false });
      } while (state.queued);
    };

    state.running = run().finally(() => {
      state.running = undefined;
    });

    return state.running;
  }, [refetchOrders]);

  useEffect(() => {
    if (state.kind !== "authenticated") return;

    const resynchronise = () => {
      void refresh();
    };

    const events = new OrderEvents({
      config: api.config,
      credential: () => api.credential(),
      channel: "/default/channel",
      state: setConnection,
      resynchronise,
      event: (value) => {
        const parsed = decodeUnknownOption(eventSchema, value);

        if (parsed) merge([parsed.payload]);
        else resynchronise();
      },
    });

    events.start();

    const reconnect = () => {
      events.stop();
      events.start();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") reconnect();
    };

    window.addEventListener("online", reconnect);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      events.stop();
      window.removeEventListener("online", reconnect);
      document.removeEventListener("visibilitychange", onVisibility);
      queryClient.removeQueries({ queryKey: activeOrdersQueryKey });
    };
  }, [api, state, refresh, merge, queryClient]);

  return (
    <OrdersContext.Provider
      value={{
        orders,
        query: ordersQuery,
        connection,
        refresh,
        merge,
        notice,
        setNotice,
        closure,
        recordClosure,
        dismissClosure,
      }}
    >
      {children}
    </OrdersContext.Provider>
  );
}

/** Read and update the shared order cache. */
export function useOrders() {
  const value = useContext(OrdersContext);

  if (!value) throw new Error("OrdersProvider is missing.");

  return value;
}
